import React, { ForwardRefRenderFunction, useCallback } from "react";
//#region types
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { FormikProps } from "formik";
import { FormHandles } from "models/propTypes/genericFormProps";
import { InvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { FormFieldConstants, InvoiceConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { QuotationPreviewFormContainerProps } from "models/propTypes/quotationPropTypes";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useInvoiceCreateContext } from "components/invoice";
import { useDebouncedCallback } from "use-debounce";
import QuotationPreviewForm from "./previewForm";
import { useCustomerService, useQuotesService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import * as Yup from "yup";

const QuotationPreviewFormContainer: ForwardRefRenderFunction<FormHandles, QuotationPreviewFormContainerProps> = (
  props,
  ref
) => {
  const quotesService = useQuotesService();
  const customerService = useCustomerService();

  const { serialNumber, quotationPost, referencePersonName, customerId, isDraft, defaultSendType } = props;

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () =>
    formRef.current
      ? {
          ...formRef.current,
        }
      : ({} as FormHandles)
  );

  const serialNumberTemplateValueDisplayText = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Quotation.QuotationNumberHashTag
  );

  const invoiceCreateContext = useInvoiceCreateContext();
  const [mailForm, setMailForm] = React.useState<WintSuperkollAPIModel.InvoiceDefaultMailOptions>();

  const loadSendMailOptions = async () => {
    const res = await quotesService.quotationMailForm({}, serialNumber);

    const result = res;
    if (quotationPost) {
      const customer = await customerService.getCustomer(quotationPost.customerId);
      const mailToList: WintSuperkollAPIModel.InvoiceMailRecipientOptions[] = [];

      if (quotationPost.customerReference?.referenceId) {
        const referenceEmail = customer.referencePersonsList?.find(
          (x) => x.id === quotationPost.customerReference?.referenceId
        )?.emailAddress;

        referenceEmail &&
          mailToList.push({
            type: WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other,
            address: referenceEmail,
          });
      }

      customer.emailAddress &&
        mailToList.push({
          type: WintSuperkollAPIModel.SendInvoiceMailsToEnum.CustomerCompany,
          address: customer.emailAddress,
        });

      result.to = mailToList[0];
      result.mailToList = mailToList;
    }
    result.subject = `${res.subject}${!serialNumber ? InvoiceConstants.SerialNumberTemplateValue : ""}`;

    setMailForm(result);
    formRef.current?.setValues &&
      formRef.current.setValues<InvoiceFormTypes.QuotationPreview>({
        sendMail: {
          emailFromType: result?.from?.type,
          emailFrom: result?.from?.address,
          emailTo: result?.to
            ? UtilsHelper.splitMultipleEmails(result.to?.address).map((email) => ({
                id: `${result?.to?.type}_${email}`,
                value: email,
              }))
            : [],
          emailSubject: result?.subject?.replace(
            InvoiceConstants.SerialNumberTemplateValue,
            serialNumberTemplateValueDisplayText
          ),
          emailMessage: result?.message,
        },
        loading: false,
      });
  };

  React.useEffect(() => {
    loadSendMailOptions();
    loadQuotationTemplate(initialValues);
  }, [serialNumber]);

  const initialValues: InvoiceFormTypes.QuotationPreview = {
    sendType: defaultSendType ?? WintSuperkollAPIModel.InvoiceSendMethod.Email,
    sendMail: {},
    notes: quotationPost?.notes,
    attachments: quotationPost?.attachments as UploadedFile[],
  };

  const validationSchema = Yup.object<InvoiceFormTypes.QuotationPreview>({
    sendType: Yup.number().required(TranslationKeys.Validations.Required),
    sendMail: Yup.object().when(
      FormFieldConstants.Invoice.Preview.SendType,
      ([sendType], schema: ObjectSchema<InvoiceFormTypes.SendMail>) =>
        sendType === WintSuperkollAPIModel.InvoiceSendMethod.Email
          ? Yup.object<InvoiceFormTypes.SendMail>({
              emailFrom: Yup.string().required(TranslationKeys.Validations.Required),
              emailFromType: Yup.number(),
              emailTo: Yup.array()
                .of(
                  Yup.object<AutocompleteListItem>({
                    id: Yup.string(),
                    value: Yup.string().email(TranslationKeys.Validations.InvalidEmail),
                  })
                )
                .compact((v: AutocompleteListItem) => !v || !v.value)
                .required(TranslationKeys.Validations.Required)
                .min(1, TranslationKeys.Validations.AtLeastOneItem)
                .unique(TranslationKeys.Validations.EmailNotUnique, "value"),
              emailSubject: Yup.string().required(TranslationKeys.Validations.Required),
              emailMessage: Yup.string(),
            })
          : schema
    ),
    notes: Yup.string(),
    attachments: Yup.array().of(
      Yup.object<UploadedFile>({
        contentType: Yup.string(),
        fileName: Yup.string(),
        fileSize: Yup.number(),
        data: Yup.string(),
      })
    ),
    loading: Yup.boolean(),
  });

  const loadQuotationTemplate = useDebouncedCallback((values: InvoiceFormTypes.QuotationPreview) => {
    if (quotationPost) {
      quotesService
        .generateQuotationPreview({
          ...quotationPost,
          notes: values.notes,
          serialNumber: serialNumber ?? 0,
        })
        .then((res) => {
          invoiceCreateContext.setInvoiceHtml(res.data as unknown as string);
        });
    } else if (serialNumber) {
      quotesService.getQuotationPDFPreview(serialNumber).then((res) => {
        invoiceCreateContext.setInvoiceHtml(res.data as unknown as string);
      });
    }
  }, 1500);

  const onNotesChangeHandler = useCallback(
    (name: string, updatedValue: any, formikProps: FormikProps<InvoiceFormTypes.QuotationPreview>) => {
      loadQuotationTemplate(formikProps.values);
    },
    [loadQuotationTemplate]
  );

  return (
    <QuotationPreviewForm
      ref={formRef}
      defaultValues={initialValues}
      serialNumberTemplateValueDisplayText={serialNumberTemplateValueDisplayText}
      serialNumber={serialNumber}
      customerId={customerId}
      referencePersonName={referencePersonName}
      mailForm={mailForm ?? {}}
      onNotesChangeHandler={onNotesChangeHandler}
      validationSchema={validationSchema}
      onFormValidationChange={props.onFormValidationChange}
      isDraft={isDraft}
    />
  );
};

export default React.forwardRef(QuotationPreviewFormContainer);
